import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_LAYERS_LIST, InferenceStatus, LayerAlias } from "constant";
import { LayersManagerStateInterface } from "./interfaces";
import {
  combineLayersList,
  combineProviderList,
  combineProvidersStatusesList
} from "./helpers";

const initialState: LayersManagerStateInterface = {
  isCreateProviderLoading: false,
  isReorderProviderLoading: false,
  isProviderPhotosLoading: false,
  providers: [],
  layers: DEFAULT_LAYERS_LIST,
  providersCompletionDisconnectExecutor: null,
  providersStatusesDisconnectExecutor: null,
  completionMessages: []
};

export const mainSlice = createSlice({
  name: "layersManager",
  initialState,
  reducers: {
    setLayers: (state, { payload }) => {
      const combinedLayers = combineLayersList(state.layers, payload.layers);
      state.layers = combinedLayers;
    },
    setStreamCompletionDisconnectExecutor: (state, { payload }) => {
      if (payload.params.id) {
        state.providersCompletionDisconnectExecutor =
          payload.disconnectExecutor;
      }
    },
    setCompletionEvent: (state, { payload }) => {
      state.completionMessages = [...state.completionMessages, payload];
    },
    startSensorFusingInferenceRequest: (state, { payload }) => {
      const { alias } = payload.params;

      state.layers = state.layers.map((layer) =>
        layer.alias === alias
          ? {
              ...layer,
              isInferenceStatusWaiting: true
            }
          : layer
      );
    },
    startSensorFusingInferenceSuccess: (state, { payload }) => {
      const { alias } = payload.params;

      state.layers = state.layers.map((layer) =>
        layer.alias === alias
          ? {
              ...layer,
              isInferenceStatusWaiting: false
            }
          : layer
      );
    },
    startSensorFusingInferenceError: (state, { payload }) => {
      const { alias } = payload.params;

      state.layers = state.layers.map((layer) =>
        layer.alias === alias
          ? {
              ...layer,
              isInferenceStatusWaiting: false
            }
          : layer
      );
    },

    cancelSensorFusingInferenceRequest: (state, { payload }) => {
      const { alias } = payload.params;
      state.layers = state.layers.map((layer) =>
        layer.alias === alias
          ? {
              ...layer,
              inference: { ...layer.inference, isInferenceLoading: true }
            }
          : layer
      );
    },
    cancelSensorFusingInferenceSuccess: (state, { payload }) => {
      const { alias } = payload.params;
      state.layers = state.layers.map((layer) =>
        layer.alias === alias
          ? {
              ...layer,
              inference: {
                ...layer.inference,
                isInferenceLoading: false,
                status: InferenceStatus.STOPPING
              }
            }
          : layer
      );
    },
    cancelSensorFusingInferenceError: (state, { payload }) => {
      const { alias } = payload.params;

      state.layers = state.layers.map((layer) =>
        layer.alias === alias
          ? {
              ...layer,
              inference: { ...layer.inference, isInferenceLoading: false }
            }
          : layer
      );
    },

    setLayerFilter: (state, { payload }) => {
      state.layers = state.layers.map((item) => {
        return item.alias === payload.alias
          ? { ...item, filter: payload.filter }
          : item;
      });
    },
    setLayerOpacity: (state, { payload }) => {
      state.layers = state.layers.map((item) => {
        return item.alias === payload.alias
          ? { ...item, opacity: payload.opacity }
          : item;
      });
    },

    setLayerIsActive: (state, { payload }) => {
      state.layers = state.layers.map((item) =>
        item.alias === payload.params.alias
          ? { ...item, isActive: payload.fields?.isActive }
          : item
      );
    },

    //TODO :)
    setProviderFilterPercentiles: (state, { payload }) => {
      state.layers = state.layers.map((layer) =>
        layer.alias === payload.alias
          ? {
              ...layer,
              providers: layer.providers.map((provider) =>
                provider.id === payload.providerId
                  ? {
                      ...provider,
                      data: {
                        ...provider.data,
                        filters: provider.data.filters.map((filter) =>
                          filter.formula === layer.filter.name
                            ? {
                                ...filter,
                                statistics: {
                                  ...filter.statistics,
                                  percentiles:
                                    payload.percentiles ??
                                    filter.statistics.originalPercentiles,
                                  originalPercentiles:
                                    filter.statistics.originalPercentiles &&
                                    payload.percentiles
                                      ? filter.statistics.originalPercentiles
                                      : payload.percentiles
                                      ? filter.statistics.percentiles
                                      : undefined
                                }
                              }
                            : filter
                        )
                      }
                    }
                  : provider
              )
            }
          : layer
      );
    },

    setProviders: (state, { payload }) => {
      const isLayerActive = state.layers.find(
        (el) => el.alias === payload.params.alias
      ).isActive;

      const providers =
        payload.providers?.map((provider) => {
          return {
            ...provider,
            isInferenceStatusWaiting: true,
            isActiveMarkers: provider.isActiveMarkers ?? isLayerActive,
            isActiveTiles: provider.isActiveTiles ?? isLayerActive
          };
        }) ?? [];

      state.providers = providers;
    },
    setProvidersStatuses: (state, { payload }) => {
      if (payload?.[0]?.data?.agent === LayerAlias.SENSOR_FUSION) {
        //  TODO: update status of sensor fusion layer in another way(for preventing markers re-renders)
        state.layers = state.layers.map((layer) => {
          return layer.alias === LayerAlias.SENSOR_FUSION
            ? { ...layer, inference: payload[0] }
            : layer;
        });
      } else {
        const providersWithUpdatedStatuses = combineProvidersStatusesList(
          state.providers,
          payload
        );
        state.providers = providersWithUpdatedStatuses;
      }
    },
    setStreamStatusesDisconnectExecutor: (state, { payload }) => {
      if (payload.params.alias) {
        state.providersStatusesDisconnectExecutor = payload.disconnectExecutor;
      }
    },
    updateLayerProviders: (state, { payload }) => {
      state.layers = state.layers.map((layer) =>
        layer.alias === payload.alias
          ? {
              ...layer,
              providers: payload.providers
            }
          : layer
      );
    },

    setProvidersMarkersIsActive: (state, { payload }) => {
      state.providers = state.providers.map((item) =>
        item.id === payload.providerId
          ? { ...item, isActiveMarkers: payload?.isActiveMarkers }
          : item
      );
    },
    setAllProvidersMarkersIsActive: (state, { payload }) => {
      state.providers = state.providers.map((item) => ({
        ...item,
        isActiveMarkers: payload?.isActiveMarkers
      }));
    },
    setProvidersTilesIsActive: (state, { payload }) => {
      state.providers = state.providers.map((item) =>
        item.id === payload.providerId
          ? { ...item, isActiveTiles: payload?.isActiveTiles }
          : item
      );
    },
    setAllProvidersTilesIsActive: (state, { payload }) => {
      state.providers = state.providers.map((item) => ({
        ...item,
        isActiveTiles: payload?.isActiveTiles
      }));
    },

    createProviderRequest: (state) => {
      state.isCreateProviderLoading = true;
    },
    setUploadInferenceFileCancelEvent: (state, { payload }) => {
      const { cancelExecutor, params } = payload;
      const { providerId } = params;

      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              inference: {
                ...provider.inference,
                status: InferenceStatus.UPLOADING,
                data: {
                  stats: { upload_time_start_at: new Date() }
                },
                fileUploadData: {
                  ...provider.inference?.fileUploadData,
                  cancelExecutor
                }
              }
            }
          : provider;
      });
    },
    setUploadInferenceFileProgress: (state, { payload }) => {
      const { progress, params } = payload;
      const { providerId } = params;

      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              inference: {
                ...provider.inference,
                fileUploadData: {
                  ...provider.inference.fileUploadData,
                  progress
                }
              }
            }
          : provider;
      });
    },
    cancelUploadingInferenceFile: (state, { payload }) => {
      const { providerId } = payload.params;

      state.providers = state.providers.map((provider) => {
        if (provider.id === providerId) {
          provider.inference.fileUploadData.cancelExecutor();

          return {
            ...provider,
            inference: undefined
          };
        }
        return provider;
      });
    },

    createProviderSuccess: (state, { payload }) => {
      state.providers = combineProviderList(state.providers, payload.providers);

      state.isCreateProviderLoading = false;
    },
    createProviderError: (state) => {
      state.isCreateProviderLoading = false;
    },

    deleteProviderRequest: (state, { payload }) => {
      const providerId = payload.params.providerId;

      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              isDeleteProviderLoading: true
            }
          : provider;
      });
    },
    deleteProviderSuccess: (state, { payload }) => {
      state.providers = combineProviderList(
        state.providers,
        payload?.providers ?? []
      );
    },
    deleteProviderError: (state, { payload }) => {
      const providerId = payload.params.providerId;

      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              isDeleteProviderLoading: false
            }
          : provider;
      });
    },

    reorderProviderRequest: (state, { payload }) => {
      const fromIndex = state.providers.find((item) => {
        return item.id.toString() === payload.params.providerId;
      }).index;
      const toIndex = payload.fields.index;
      const newArray = [...state.providers];
      const [movedElement] = newArray.splice(fromIndex, 1);
      newArray.splice(toIndex, 0, movedElement);

      state.providers = newArray;

      state.isReorderProviderLoading = true;
    },
    reorderProviderSuccess: (state, { payload }) => {
      state.isReorderProviderLoading = false;
      state.providers = combineProviderList(state.providers, payload.providers);
    },
    reorderProviderError: (state) => {
      state.isReorderProviderLoading = false;
    },

    startProviderInferenceRequest: (state, { payload }) => {
      const { params } = payload;
      const { providerId } = params;

      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              isInferenceStatusWaiting: true
            }
          : provider;
      });
    },
    //Statuses updated by sse events
    startProviderInferenceSuccess: () => {},
    //Statuses updated by sse events
    startProviderInferenceError: () => {},

    cancelProviderInferenceRequest: (state, { payload }) => {
      const { params } = payload;
      const { providerId } = params;

      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              isInferenceStatusWaiting: true
            }
          : provider;
      });
    },
    //Statuses updated by sse events
    cancelProviderInferenceSuccess: () => {},
    //Statuses updated by sse events
    cancelProviderInferenceError: () => {},

    fetchProvidersPhotosRequest: (state) => {
      state.isProviderPhotosLoading = true;
    },
    fetchProvidersPhotosSuccess: (state, { payload }) => {
      state.isProviderPhotosLoading = false;

      const { params } = payload;
      const { provider: providerId } = params;
      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              photos: payload.data.images
            }
          : provider;
      });
    },
    fetchProvidersPhotosError: (state) => {
      state.isProviderPhotosLoading = false;
    },

    //Temporary only for RGB2
    prepareProviderInferenceRequest: () => {},

    uploadProviderFilesRequest: (state, { payload }) => {
      const { params } = payload;
      const { providerId } = params;

      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              inference: {
                ...provider.inference,
                status: InferenceStatus.UPLOADING
              },
              photos: [
                ...(provider.photos ?? []),
                ...payload.fields.getAll("files[]")
              ]
            }
          : provider;
      });
    },
    // setUploadProviderFilesCancelEvent: (state, { payload }) => {
    //   console.log(state, payload);
    // },
    // setUploadProviderFilesProgress: (state, { payload }) => {
    //   console.log(state, payload);
    // },
    uploadProviderFilesSuccess: (state, { payload }) => {
      const { params } = payload;
      const { providerId } = params;

      state.providers = state.providers.map((provider) => {
        return provider.id === providerId
          ? {
              ...provider,
              inference: {
                ...provider.inference,
                status: null
              },
              photos: provider.photos.map((photo) => {
                return {
                  ...photo,
                  name: photo.name,
                  status: "available"
                };
              })
            }
          : provider;
      });
    },

    clearProvidersState: (state, { payload }) => {
      if (state.providers.length > 0) {
        state.layers = state.layers.map((layer) =>
          layer.alias === payload
            ? { ...layer, providers: state.providers }
            : layer
        );
        state.providers = initialState.providers;
      }
    },
    clearLayersState: (state) => {
      state.layers = initialState.layers;
    },

    restoreDefaultState: () => {},
    clearState: () => initialState
  }
});

export const { actions, reducer } = mainSlice;
